import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./Profile.css";
import ServerCard from "../components/ServerCard";
import { getLocaleFromPathname } from "../utils/locale";

const BOT_INVITE_LINK =
  "https://discord.com/oauth2/authorize?client_id=1433494628711137290&permissions=8&integration_type=0&scope=bot";

function txByLocale(locale, ko, vi) {
  return locale === "vi" ? vi || ko : ko;
}

function getGuildIcon(guild, cacheBuster, className = "guild-icon") {
  const name = guild.name || guild.server_name || "?";
  if (guild.icon) {
    const extension = guild.icon.startsWith("a_") ? "gif" : "png";
    return (
      <img
        src={`https://cdn.discordapp.com/icons/${guild.id || guild.server_id}/${guild.icon}.${extension}?t=${cacheBuster}`}
        alt={name}
        className={className}
      />
    );
  }

  const initials = name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .substring(0, 3);
  return <div className={`${className}-fallback ${className}`}>{initials}</div>;
}

function getUserAvatar(user) {
  if (user?.avatar) {
    const extension = user.avatar.startsWith("a_") ? "gif" : "png";
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.${extension}`;
  }
  return "https://cdn.discordapp.com/embed/avatars/0.png";
}

function Profile() {
  const location = useLocation();
  const locale = getLocaleFromPathname(location.pathname);
  const tx = (ko, vi) => txByLocale(locale, ko, vi);

  const [user, setUser] = useState(null);
  const [unaddedGuilds, setUnaddedGuilds] = useState([]);
  const [myServers, setMyServers] = useState([]);
  const [editableData, setEditableData] = useState({});
  const [selectedGuild, setSelectedGuild] = useState("");
  const [showInviteButtonAfterScroll, setShowInviteButtonAfterScroll] = useState(false);

  const [loadingGuilds, setLoadingGuilds] = useState(false);
  const [loadingServers, setLoadingServers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [applyingBackground, setApplyingBackground] = useState({});
  const [shopProducts, setShopProducts] = useState([]);
  const [shopPurchases, setShopPurchases] = useState([]);
  const [cacheBuster, setCacheBuster] = useState(Date.now());

  const guildListRef = useRef(null);

  const shopProductById = useMemo(() => {
    const next = {};
    shopProducts.forEach((product) => {
      if (product?.id) next[product.id] = product;
    });
    return next;
  }, [shopProducts]);

  const ownedShopProducts = useMemo(
    () =>
      shopPurchases
        .filter((purchase) => purchase.isActive !== false)
        .map((purchase) => shopProductById[purchase.productId || purchase.product_id])
        .filter(Boolean),
    [shopPurchases, shopProductById]
  );

  useEffect(() => {
    const next = {};
    myServers.forEach((server) => {
      next[server.server_id] = {
        description: server.description || "",
        tags: server.tags || "",
        is_nsfw: server.is_nsfw || false,
        font: server.font || "default",
        show_comments: server.show_comments || false,
        shop_background_product_id: server.shop_background_product_id || "",
      };
    });
    setEditableData(next);
  }, [myServers]);

  const refreshAllData = () => {
    setLoadingGuilds(true);
    setLoadingServers(true);
    setCacheBuster(Date.now());

    fetch("https://disparty.com/api/guilds", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => setUnaddedGuilds(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to fetch guilds:", err))
      .finally(() => setLoadingGuilds(false));

    fetch("https://disparty.com/api/servers/mine", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => setMyServers(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to fetch my servers:", err))
      .finally(() => setLoadingServers(false));

    fetch("https://disparty.com/api/shop/products", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => setShopProducts(Array.isArray(data) ? data : []))
      .catch(() => setShopProducts([]));

    fetch("https://disparty.com/api/shop/purchases", { credentials: "include" })
      .then((res) => {
        if (res.status === 401) return [];
        return res.ok ? res.json() : Promise.reject(res);
      })
      .then((data) => setShopPurchases(Array.isArray(data) ? data : []))
      .catch(() => setShopPurchases([]));
  };

  useEffect(() => {
    fetch("https://disparty.com/me", { credentials: "include" })
      .then((res) => (res.status === 200 ? res.json() : Promise.reject(res)))
      .then((data) => {
        setUser(data);
        refreshAllData();
      })
      .catch(() => {
        setUser(null);
        setUnaddedGuilds([]);
        setMyServers([]);
        setShopPurchases([]);
      });
  }, []);

  const handleLogin = () => {
    window.location.href = "https://disparty.com/login";
  };

  const handleLogout = () => {
    window.location.href = "https://disparty.com/logout";
  };

  const handleEditDataChange = (serverID, field, value) => {
    setEditableData((prev) => ({
      ...prev,
      [serverID]: {
        ...prev[serverID],
        [field]: value,
      },
    }));
  };

  const handleAddServer = () => {
    if (!selectedGuild) return;
    setIsSubmitting(true);

    fetch("https://disparty.com/api/servers/add", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ server_id: selectedGuild }),
    })
      .then(async (res) => {
        if (res.status === 201) {
          alert(tx("서버가 성공적으로 등록되었습니다.", "Đăng ký server thành công."));
          setSelectedGuild("");
          refreshAllData();
          return;
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || tx("서버 추가에 실패했습니다.", "Thêm server thất bại."));
      })
      .catch((err) => {
        console.error("Failed to add server:", err);
        alert(err.message || tx("서버 추가 중 오류가 발생했습니다.", "Đã xảy ra lỗi khi thêm server."));
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleBannerUpload = (serverID, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert(tx("파일 크기는 5MB를 초과할 수 없습니다.", "Kích thước tệp không được vượt quá 5MB."));
      return;
    }

    setUploading((prev) => ({ ...prev, [serverID]: true }));
    const formData = new FormData();
    formData.append("banner_image", file);

    fetch(`https://disparty.com/api/servers/${serverID}/upload`, {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then(async (res) => {
        if (res.ok) {
          alert(tx("배너가 성공적으로 업로드되었습니다.", "Tải banner thành công."));
          refreshAllData();
          return;
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || tx("업로드에 실패했습니다.", "Tải lên thất bại."));
      })
      .catch((err) => {
        console.error("Failed to upload banner:", err);
        alert(err.message || tx("업로드 중 오류가 발생했습니다.", "Đã xảy ra lỗi khi tải lên."));
      })
      .finally(() => {
        setUploading((prev) => ({ ...prev, [serverID]: false }));
        const input = document.getElementById(`banner-upload-${serverID}`);
        if (input) input.value = null;
      });
  };

  const handleApplyShopBackground = (serverID, productID) => {
    handleEditDataChange(serverID, "shop_background_product_id", productID);
    setApplyingBackground((prev) => ({ ...prev, [serverID]: true }));

    fetch("https://disparty.com/api/shop/background/apply", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ serverId: serverID, productId: productID }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || data.message || tx("서버 배경 적용에 실패했습니다.", "Áp dụng nền server thất bại."));
        }
      })
      .then(() => refreshAllData())
      .catch((err) => {
        alert(err.message || tx("서버 배경 적용 중 오류가 발생했습니다.", "Đã xảy ra lỗi khi áp dụng nền server."));
        refreshAllData();
      })
      .finally(() => setApplyingBackground((prev) => ({ ...prev, [serverID]: false })));
  };

  const handleSaveChanges = (serverID) => {
    const dataToSave = editableData[serverID];
    if (!dataToSave) return;

    const tagsArray = dataToSave.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    if (tagsArray.length > 5) {
      alert(tx("태그는 최대 5개까지 설정할 수 있습니다.", "Tối đa 5 tag."));
      return;
    }

    for (const tag of tagsArray) {
      if (tag.length > 5) {
        alert(tx(`태그 '${tag}'는 5글자 이내로 작성해주세요.`, `Tag '${tag}' tối đa 5 ký tự.`));
        return;
      }
    }

    if (dataToSave.description.length > 500) {
      alert(tx("서버 소개는 500자 이내로 작성해주세요.", "Mô tả server tối đa 500 ký tự."));
      return;
    }

    setSaving((prev) => ({ ...prev, [serverID]: true }));
    fetch(`https://disparty.com/api/servers/${serverID}/update`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        description: dataToSave.description,
        tags: tagsArray,
        is_nsfw: dataToSave.is_nsfw || false,
        font: dataToSave.font || "default",
        show_comments: dataToSave.show_comments || false,
      }),
    })
      .then(async (res) => {
        if (res.ok) {
          alert(tx('서버 정보가 저장되었습니다.\n처음 저장했다면 Discord 서버에서 "/party" 명령어를 사용해 피드에 반영해주세요.', 'Đã lưu thông tin server.\nNếu là lần đầu lưu, hãy dùng lệnh "/party" trong server Discord để hiển thị trên feed.'));
          refreshAllData();
          return;
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || tx("저장에 실패했습니다.", "Lưu thất bại."));
      })
      .catch((err) => {
        console.error("Failed to save changes:", err);
        alert(err.message || tx("저장 중 오류가 발생했습니다.", "Đã xảy ra lỗi khi lưu."));
      })
      .finally(() => setSaving((prev) => ({ ...prev, [serverID]: false })));
  };

  const handleDeleteServer = (serverID) => {
    const serverName = myServers.find((server) => server.server_id === serverID)?.server_name || tx("이 서버", "server này");

    if (
      !window.confirm(
        tx(
          `정말로 '${serverName}' 서버를 목록에서 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`,
          `Bạn có chắc muốn xóa '${serverName}' khỏi danh sách?\nHành động này không thể hoàn tác.`
        )
      )
    ) {
      return;
    }

    setDeleting((prev) => ({ ...prev, [serverID]: true }));
    fetch(`https://disparty.com/api/servers/${serverID}`, {
      method: "DELETE",
      credentials: "include",
    })
      .then(async (res) => {
        if (res.ok) {
          alert(tx("서버가 성공적으로 삭제되었습니다.", "Đã xóa server thành công."));
          refreshAllData();
          return;
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || tx("삭제에 실패했습니다.", "Xóa thất bại."));
      })
      .catch((err) => {
        console.error("Failed to delete server:", err);
        alert(err.message || tx("삭제 중 오류가 발생했습니다.", "Đã xảy ra lỗi khi xóa."));
      })
      .finally(() => setDeleting((prev) => ({ ...prev, [serverID]: false })));
  };

  const handleGuildListScroll = () => {
    const listElement = guildListRef.current;
    if (!listElement) return;
    const isBottom = listElement.scrollTop + listElement.clientHeight >= listElement.scrollHeight - 8;
    if (isBottom) setShowInviteButtonAfterScroll(true);
  };

  useEffect(() => {
    if (loadingGuilds || unaddedGuilds.length === 0) {
      setShowInviteButtonAfterScroll(false);
      return;
    }
    const listElement = guildListRef.current;
    if (!listElement) return;
    setShowInviteButtonAfterScroll(listElement.scrollTop + listElement.clientHeight >= listElement.scrollHeight - 8);
  }, [loadingGuilds, unaddedGuilds]);

  if (!user) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <div className="profile-login">
            <h1>Disparty</h1>
            <p>{tx("서버를 등록하려면 Discord 로그인이 필요합니다.", "Cần đăng nhập Discord để đăng ký server.")}</p>
            <button onClick={handleLogin} className="btn-discord">
              {tx("Discord로 로그인", "Đăng nhập bằng Discord")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <h1>{tx("서버 관리", "Quản lý server")}</h1>
          <div className="profile-userinfo">
            <img src={getUserAvatar(user)} alt="avatar" className="profile-avatar" />
            <span>{tx(`${user.username}님 환영합니다`, `Xin chào, ${user.username}`)}</span>
            <button onClick={handleLogout} className="btn-logout">
              {tx("로그아웃", "Đăng xuất")}
            </button>
          </div>
        </div>

        <section className="profile-section">
          <h3>
            {tx("신규 서버 등록", "Đăng ký server mới")}
            <span style={{ fontSize: "12px", color: "#b9bbbe", marginLeft: "10px", fontWeight: "normal" }}>
              {tx("처음 저장한 뒤 Discord 서버에서 /party 명령어를 사용해주세요.", "Sau khi lưu lần đầu, hãy dùng lệnh /party trong server Discord.")}
            </span>
          </h3>

          {loadingGuilds ? (
            <p className="loading-text">{tx("등록 가능한 서버 목록을 불러오는 중...", "Đang tải danh sách server có thể đăng ký...")}</p>
          ) : unaddedGuilds.length > 0 ? (
            <>
              <div className="guild-list" ref={guildListRef} onScroll={handleGuildListScroll}>
                {unaddedGuilds.map((guild) => (
                  <button
                    key={guild.id}
                    className={`guild-item ${selectedGuild === guild.id ? "selected" : ""}`}
                    onClick={() => setSelectedGuild(guild.id)}
                  >
                    {getGuildIcon(guild, cacheBuster)}
                    <span className="guild-name">{guild.name}</span>
                  </button>
                ))}
              </div>
              {showInviteButtonAfterScroll && (
                <div className="bot-invite-info only-button before-submit">
                  <a href={BOT_INVITE_LINK} target="_blank" rel="noopener noreferrer" className="btn-discord-small full-width">
                    {tx("Disparty 봇 초대하기", "Mời bot Disparty")}
                  </a>
                </div>
              )}
              <button onClick={handleAddServer} className="btn-submit" disabled={!selectedGuild || isSubmitting}>
                {isSubmitting ? tx("등록 중...", "Đang đăng ký...") : tx("선택한 서버 등록하기", "Đăng ký server đã chọn")}
              </button>
            </>
          ) : (
            <>
              <p className="info-text">
                {tx("등록할 수 있는 서버가 없습니다.", "Không có server nào có thể đăng ký.")}
                <br />
                {tx("서버 소유자 또는 관리자 권한이 있고 봇이 서버에 초대되어 있는지 확인해주세요.", "Hãy kiểm tra quyền quản trị và bot đã được mời vào server.")}
              </p>
              <div className="bot-invite-info only-button">
                <a href={BOT_INVITE_LINK} target="_blank" rel="noopener noreferrer" className="btn-discord-small">
                  {tx("Disparty 봇 초대하기", "Mời bot Disparty")}
                </a>
              </div>
            </>
          )}
        </section>

        <section className="profile-section">
          <h3>{tx("내 서버 관리", "Quản lý server của tôi")}</h3>
          {loadingServers ? (
            <p className="loading-text">{tx("등록된 서버 목록을 불러오는 중...", "Đang tải danh sách server đã đăng ký...")}</p>
          ) : myServers.length > 0 ? (
            <div className="my-server-list">
              {myServers.map((server) => {
                const edit = editableData[server.server_id] || {
                  description: "",
                  tags: "",
                  is_nsfw: false,
                  font: "default",
                  show_comments: false,
                  shop_background_product_id: server.shop_background_product_id || "",
                };
                const bannerUrl = server.banner_image
                  ? `https://disparty.com/uploads/${server.banner_image}?t=${cacheBuster}`
                  : "none";
                const isDisabled =
                  saving[server.server_id] ||
                  deleting[server.server_id] ||
                  uploading[server.server_id] ||
                  applyingBackground[server.server_id];

                return (
                  <div key={server.server_id} className="server-card-manage">
                    <div className="card-banner" style={{ backgroundImage: bannerUrl !== "none" ? `url(${bannerUrl})` : "none" }}>
                      <label htmlFor={`banner-upload-${server.server_id}`} className={`btn-upload-label card-banner-upload-btn ${isDisabled ? "disabled" : ""}`}>
                        {uploading[server.server_id] ? "..." : tx("배너 변경", "Đổi banner")}
                      </label>
                      <input
                        type="file"
                        id={`banner-upload-${server.server_id}`}
                        className="banner-upload-input"
                        accept=".png,.jpg,.jpeg,.gif,.webp"
                        disabled={isDisabled}
                        onChange={(e) => handleBannerUpload(server.server_id, e.target.files[0])}
                      />
                      <p className="banner-upload-warning">
                        {tx("권장 1200×540px (20:9). 다른 비율은 가장자리가 잘릴 수 있으며, 변경 후 즉시 반영되지 않을 수 있습니다.", "Khuyến nghị 1200×540px (20:9). Tỷ lệ khác có thể bị cắt và thay đổi có thể chưa hiển thị ngay.")}
                      </p>
                    </div>

                    <div className="card-body">
                      <div className="card-avatar">{getGuildIcon(server, cacheBuster, "card-avatar-img")}</div>
                      <div className="card-info">
                        <h4 className="card-title">{server.server_name}</h4>

                        <div className="card-form-group-toggle">
                          <label htmlFor={`nsfw-toggle-${server.server_id}`} className="toggle-label">
                            {tx("NSFW (18+) 서버", "Server NSFW (18+)")}
                          </label>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              id={`nsfw-toggle-${server.server_id}`}
                              checked={!!edit.is_nsfw}
                              disabled={isDisabled}
                              onChange={(e) => handleEditDataChange(server.server_id, "is_nsfw", e.target.checked)}
                            />
                            <span className="toggle-slider" />
                          </label>
                        </div>
                        <p className="nsfw-warning-text">
                          {tx("성인 콘텐츠가 포함된 서버라면 반드시 활성화해주세요.", "Bật tùy chọn này nếu server có nội dung người lớn.")}
                        </p>

                        <div className="card-form-group-toggle" style={{ marginTop: "10px" }}>
                          <label htmlFor={`comments-toggle-${server.server_id}`} className="toggle-label">
                            {tx("익명 댓글창 표시", "Hiển thị bình luận ẩn danh")}
                          </label>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              id={`comments-toggle-${server.server_id}`}
                              checked={!!edit.show_comments}
                              disabled={isDisabled}
                              onChange={(e) => handleEditDataChange(server.server_id, "show_comments", e.target.checked)}
                            />
                            <span className="toggle-slider" />
                          </label>
                        </div>
                        <p className="nsfw-warning-text" style={{ color: "#b9bbbe", marginBottom: "15px" }}>
                          {tx("활성화하면 피드 서버 카드에 익명 댓글 버튼이 표시됩니다.", "Nếu bật, nút bình luận ẩn danh sẽ hiển thị trên thẻ server.")}
                        </p>

                        <div className="card-form-group">
                          <label htmlFor={`tags-${server.server_id}`}>{tx("태그 (쉼표로 최대 5개)", "Tag (tối đa 5, cách nhau bằng dấu phẩy)")}</label>
                          <input
                            type="text"
                            id={`tags-${server.server_id}`}
                            className="card-input"
                            placeholder={tx("예: 친목, 게임, 음악", "VD: trò chuyện, game, âm nhạc")}
                            value={edit.tags}
                            disabled={isDisabled}
                            onChange={(e) => handleEditDataChange(server.server_id, "tags", e.target.value)}
                          />
                        </div>

                        <div className="card-form-group">
                          <label htmlFor={`font-select-${server.server_id}`}>{tx("폰트 스타일", "Kiểu chữ")}</label>
                          <select
                            id={`font-select-${server.server_id}`}
                            className="card-input"
                            value={edit.font}
                            disabled={isDisabled}
                            onChange={(e) => handleEditDataChange(server.server_id, "font", e.target.value)}
                            style={{ appearance: "auto", cursor: "pointer" }}
                          >
                            <option value="default">{tx("기본 폰트", "Mặc định")}</option>
                            <option value="maple">{tx("메이플스토리", "Maple")}</option>
                            <option value="pretendard">{tx("프리텐다드", "Pretendard")}</option>
                          </select>
                        </div>

                        <div className="card-form-group">
                          <label htmlFor={`shop-background-${server.server_id}`}>{tx("상점 서버 배경", "Nền server đã mua")}</label>
                          <select
                            id={`shop-background-${server.server_id}`}
                            className="card-input"
                            value={edit.shop_background_product_id || ""}
                            disabled={isDisabled || ownedShopProducts.length === 0}
                            onChange={(e) => handleApplyShopBackground(server.server_id, e.target.value)}
                            style={{ appearance: "auto", cursor: ownedShopProducts.length === 0 ? "not-allowed" : "pointer" }}
                          >
                            <option value="">{tx("기본 배경", "Mặc định")}</option>
                            {ownedShopProducts.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name || product.id}
                              </option>
                            ))}
                          </select>
                          <p className="nsfw-warning-text" style={{ color: "#b9bbbe", marginBottom: "15px" }}>
                            {ownedShopProducts.length > 0
                              ? tx("구매한 서버 배경만 선택할 수 있습니다.", "Chỉ có thể chọn nền server đã mua.")
                              : tx("구매한 서버 배경이 없습니다. 상점에서 먼저 구매해주세요.", "Chưa có nền server đã mua.")}
                          </p>
                        </div>

                        <div className="card-form-group">
                          <label htmlFor={`desc-${server.server_id}`}>{tx("서버 소개", "Giới thiệu server")}</label>
                          <div className="markdown-editor-container">
                            <textarea
                              id={`desc-${server.server_id}`}
                              className="card-textarea markdown-editor"
                              placeholder={tx("서버를 간단히 소개해주세요.", "Hãy giới thiệu ngắn gọn về server.")}
                              value={edit.description}
                              disabled={isDisabled}
                              onChange={(e) => handleEditDataChange(server.server_id, "description", e.target.value)}
                            />

                            <div className="markdown-preview-wrapper">
                              <label>{tx("미리보기 (피드에 출력되는 모습)", "Xem trước (hiển thị trên feed)")}</label>
                              <ServerCard
                                server={{
                                  ...server,
                                  description: edit.description,
                                  tags: edit.tags,
                                  is_nsfw: edit.is_nsfw,
                                  font: edit.font,
                                  show_comments: edit.show_comments,
                                  shop_background_product_id: edit.shop_background_product_id,
                                  shopProduct: shopProductById[edit.shop_background_product_id],
                                  reloaded_at: null,
                                }}
                                preview
                                previewWidth={365}
                                locale={locale}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer">
                      <button
                        className="btn-danger"
                        onClick={() => handleDeleteServer(server.server_id)}
                        disabled={isDisabled}
                      >
                        {deleting[server.server_id] ? tx("삭제 중...", "Đang xóa...") : tx("서버 삭제", "Xóa server")}
                      </button>
                      <button
                        className="btn-submit"
                        onClick={() => handleSaveChanges(server.server_id)}
                        disabled={isDisabled}
                      >
                        {saving[server.server_id] ? tx("저장 중...", "Đang lưu...") : tx("서버 정보 저장", "Lưu thông tin server")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="info-text">{tx("아직 등록된 서버가 없습니다.", "Chưa có server nào đã đăng ký.")}</p>
          )}
        </section>
      </div>
    </div>
  );
}

export default Profile;
